/**
 * SocialPulseCallback — /auth/socialpulse-callback.
 *
 * Runs the token exchange, hands the id_token to FoodPulse's own
 * /api/auth/oidc-session endpoint to mint a local session, and then
 * redirects to the `next` URL that was stored before /authorize.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { http } from "@/lib/api";
import { completeSocialPulseCallback } from "@/lib/spOidc";
import { useAuth } from "@/contexts/AuthContext";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const PURPLE = "#7C3AED";

export default function SocialPulseCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();   // some AuthContexts expose a refresh() to reload the current user
  const [status, setStatus] = useState("verifying");
  const [error, setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { id_token, access_token, next } = await completeSocialPulseCallback();
        const r = await http.post("/auth/oidc-session", { id_token, access_token });
        if (cancelled) return;
        if (r.data?.token) {
          try { localStorage.setItem("fp_auth_token", r.data.token); } catch { /* noop */ }
        }
        if (typeof refresh === "function") await refresh().catch(() => {});
        setStatus("ok");
        setTimeout(() => nav(next || "/", { replace: true }), 600);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e?.response?.data?.detail || e.message || "Sign-in failed");
      }
    })();
    return () => { cancelled = true; };
  }, [nav, refresh]);

  return (
    <main className="min-h-[60vh] flex items-center justify-center" style={{ background: CREAM }} data-testid="socialpulse-callback">
      <div className="rounded-2xl border bg-white p-6 max-w-md w-full mx-4 text-center" style={{ borderColor: "#E9D8B4" }}>
        {status === "verifying" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={26} style={{ color: PURPLE }} />
            <div className="mt-3 text-sm font-semibold" style={{ color: GREEN }}>Signing you in with SocialPulse…</div>
          </>
        )}
        {status === "ok" && (
          <>
            <ShieldCheck className="mx-auto" size={28} style={{ color: GREEN }} />
            <div className="mt-3 text-sm font-semibold" style={{ color: GREEN }}>Signed in — redirecting…</div>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="mx-auto" size={28} style={{ color: "#B91C1C" }} />
            <div className="mt-3 text-sm font-semibold" style={{ color: "#7F1D1D" }}>Sign-in failed</div>
            <div className="mt-2 text-xs text-gray-700 break-words" data-testid="socialpulse-callback-error">{error}</div>
            <button onClick={() => nav("/auth", { replace: true })}
                    className="mt-4 rounded-full px-4 py-2 text-sm font-semibold"
                    style={{ background: GREEN, color: CREAM }}>
              Back to sign-in
            </button>
          </>
        )}
      </div>
    </main>
  );
}
